<script>
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import bedDark from './images/bed-dark.jpg';
	import bedLight from './images/bed-light.jpg';
	import bedPanelInstall from './images/bed-panel-install.jpg';
	import womanWithMigraine from './images/woman-with-migraine.jpg';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Button from '$lib/Components/Layout/Button/Button.svelte';
	import FeaturedCaseStudies from '$lib/PageSections/FeaturedCaseStudies/FeaturedCaseStudies.svelte';
</script>

<Section>
	<Image src={bedLight} />
	<p>
		For night shift workers like Lauren, a solid block of daytime sleep isn't a
		luxury, it's a critical necessity for maintaining health and ensuring
		alertness during long hospital shifts. Returning home after twelve grueling
		hours, Lauren craved the deep, uninterrupted eight hours of rest her body
		needed. However, the reality of a world waking up outside her window often
		made quality sleep elusive. Light seeped through her cellular shades, while
		the sounds of garbage trucks, barking dogs, and general daytime activity
		filtered into her bedroom.
	</p>
	<p>
		"The two ingredients you need for a sound sleep are darkness and quiet,"
		explained Lauren's husband, Brad. "Everything depends on a good sleep."
		Recognizing this need, they sought a solution that could transform their
		bedroom into a true daytime sanctuary.
	</p>
</Section>

<Section>
	<h2>The Indow Sleep Panel Solution</h2>
	<p>
		Brad and Lauren discovered <a href="/products/sleep-panels"
			>Indow Sleep Panels</a
		>, a custom fit solution designed specifically to block all light and
		dramatically reduce noise coming through existing windows. Unlike
		traditional transparent inserts, Sleep Panels are crafted from a solid white
		medium density fiberboard, creating a truly opaque barrier.
	</p>
	<p>
		Their decision was driven, in part, by the desire to combat noise. Before
		installation, they experienced firsthand the urban cacophony of a weekend
		night, trains rumbling, carts rolling, and neighbors socializing. "It's as
		if we're sleeping in a tent," Lauren remarked, highlighting the lack of
		soundproofing their original windows provided.
	</p>
	<Image src={bedPanelInstall} />
</Section>

<Section>
	<h2>Achieving a Restful Sleep</h2>
	<p>
		The installation of the <a href="/products/sleep-panels"
			>Indow Sleep Panels</a
		> brought an immediate and profound change. "It's completely different now," Brad
		stated. "You're in a comfortable, dark space. What we hear is what we want to
		hear - which is silence." The Sleep Panels effectively created a personal oasis
		of calm and darkness, essential for Lauren's daytime rest.
	</p>
	<p>
		Interestingly, Brad, who works during the day, also benefited significantly
		from the Sleep Panels. By blocking out moonlight, street lamps, and ambient
		noise, his nighttime sleep quality improved as well. As someone in the
		demanding field of medical equipment sales and surgery attendance, Brad
		understands the critical link between restful sleep and peak performance.
		"Any time you have a big day and have to perform, a good night's sleep is
		one of the main ingredients," he emphasized. With Indow Sleep Panels, their
		bedroom had become, in his words, "like sleeping in a soundproof box."
	</p>

	<Image src={bedDark} />

	<Bubble backgroundColor="blue" class="mt-8">
		<h3 class="text-3xl text-indow-white">Relief from Migraines</h3>
		<p>
			Individuals suffering from light sensitive migraines also understand the
			desperate search for a light free environment. Nicole endured years of
			debilitating migraines significantly worsened by light. Her efforts to
			create a dark bedroom were extensive; custom blackout blinds, heavy
			blackout curtains meticulously sealed, and even an additional decorative
			layer. Despite these measures, "complete darkness remained elusive,"
			highlighting a common and frustrating experience for the light sensitive.
			"When I don't have a headache, light is bothersome," Nicole explained.
			"When I have a headache, light is unbearable."
		</p>
		<p>
			Nicole turned to Indow for relief. With a custom-fit Sleep Panel in place,
			she finally achieved complete darkness in her room and was excited to
			share her story.
		</p>
		<p>
			“I just want to help people - I know what it’s like. If someone doesn’t
			have to suffer because they get to use this product, then I’m happy.”
		</p>
		<Image src={womanWithMigraine} />
	</Bubble>
</Section>

<Section>
	<h2>Investing in Essential Rest</h2>
	<p>
		For anyone whose sleep is compromised by unwanted light and noise, whether
		due to shift work, urban living, or sensitivities, our Sleep Panels offer a
		practical and effective solution. By creating a truly dark and quiet
		environment, Sleep Panels prioritize the essential need for restorative
		rest.
	</p>

	<Button href="/how-to/get-indow-inserts">GET AN ESTIMATE</Button>
</Section>

<FeaturedCaseStudies backgroundColor="grey-light" />
<GetAFreeEstimate />
